import React from 'react';
import styled from 'styled-components';
import { useLivePortfolioProof } from './useLivePortfolioProof';

const Section = styled.section`
  padding: 4rem 0;
  background: #ffffff;
`;

const Container = styled.div`
  max-width: 1160px;
  margin: 0 auto;
  padding: 0 1.6rem;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 1.5rem;
  align-items: end;
  margin-bottom: 1.8rem;

  @media (max-width: 800px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const HeadingBlock = styled.div`
  max-width: 720px;
`;

const Eyebrow = styled.p`
  margin: 0 0 0.7rem;
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.24em;
  text-transform: uppercase;
  color: #0f766e;
`;

const Title = styled.h2`
  margin: 0 0 0.7rem;
  font-size: 2.3rem;
  line-height: 1.05;
  color: #0f172a;
`;

const Subtitle = styled.p`
  margin: 0;
  line-height: 1.7;
  color: #475569;
`;

const PreviewTag = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 0.45rem 0.75rem;
  border-radius: 999px;
  background: ${props => props.$background};
  color: ${props => props.$color};
  font-weight: 700;
  font-size: 0.82rem;
`;

const MetaBlock = styled.div`
  display: grid;
  gap: 0.45rem;
  justify-items: end;

  @media (max-width: 800px) {
    justify-items: start;
  }
`;

const LastUpdated = styled.p`
  margin: 0;
  font-size: 0.82rem;
  color: #64748b;
`;

const Table = styled.div`
  border-radius: 24px;
  overflow: hidden;
  border: 1px solid #e2e8f0;
  box-shadow: 0 18px 50px rgba(15, 23, 42, 0.08);
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 1.4fr 1fr 0.9fr 1fr 0.9fr;
  gap: 1rem;
  padding: 1rem 1.2rem;
  background: ${props => (props.$header ? '#f8fafc' : '#ffffff')};
  border-bottom: 1px solid #e2e8f0;
  align-items: center;

  &:last-child {
    border-bottom: none;
  }

  @media (max-width: 920px) {
    grid-template-columns: 1fr;
    gap: 0.35rem;
  }
`;

const Cell = styled.div`
  color: ${props => (props.$header ? '#475569' : '#0f172a')};
  font-size: ${props => (props.$header ? '0.78rem' : '0.95rem')};
  font-weight: ${props => (props.$header ? '700' : '500')};
  letter-spacing: ${props => (props.$header ? '0.08em' : 'normal')};
  text-transform: ${props => (props.$header ? 'uppercase' : 'none')};
`;

const AgentName = styled.div`
  font-weight: 700;
  color: #0f172a;
`;

const Subline = styled.div`
  margin-top: 0.2rem;
  font-size: 0.85rem;
  color: #64748b;
`;

const StatusPill = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.35rem 0.7rem;
  border-radius: 999px;
  background: ${props => props.$background || '#e2e8f0'};
  color: ${props => props.$color || '#0f172a'};
  font-size: 0.8rem;
  font-weight: 700;
`;

const EmptyState = styled.div`
  padding: 1.6rem;
  border-radius: 24px;
  border: 1px dashed rgba(148, 163, 184, 0.44);
  background: #f8fafc;
  color: #475569;
  line-height: 1.7;
`;

const DisciplineGrid = styled.div`
  display: grid;
  gap: 1rem;
  margin-top: 1.4rem;

  @media (min-width: 900px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

const DisciplineCard = styled.article`
  padding: 1rem 1.1rem;
  border-radius: 22px;
  background: linear-gradient(180deg, rgba(8, 145, 178, 0.08) 0%, rgba(255, 255, 255, 0.96) 100%);
  border: 1px solid rgba(148, 163, 184, 0.18);
`;

const DisciplineLabel = styled.p`
  margin: 0 0 0.4rem;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  font-size: 0.75rem;
  font-weight: 700;
  color: #0f766e;
`;

const DisciplineValue = styled.p`
  margin: 0;
  color: #0f172a;
  line-height: 1.65;
  font-weight: 600;
`;

function getBadgeTone(isLoading, hasError) {
  if (isLoading) {
    return {
      background: '#e0f2fe',
      color: '#075985',
    };
  }

  if (hasError) {
    return {
      background: '#fef3c7',
      color: '#92400e',
    };
  }

  return {
    background: '#dcfce7',
    color: '#166534',
  };
}

function getStatusTone(statusLabel) {
  if (statusLabel === 'Live benchmark') {
    return {
      background: '#dcfce7',
      color: '#166534',
    };
  }

  if (statusLabel === 'Live-eligible') {
    return {
      background: '#dbeafe',
      color: '#1d4ed8',
    };
  }

  return {
    background: '#f1f5f9',
    color: '#475569',
  };
}

const LeaderboardPreview = () => {
  const { isLoading, error, rows, lastUpdatedLabel, disciplineFacts } = useLivePortfolioProof();
  const hasError = Boolean(error) || rows.length === 0;
  const badgeTone = getBadgeTone(isLoading, hasError);
  const badgeLabel = isLoading
    ? 'Loading live HedgeBench benchmark'
    : hasError
      ? 'Live data unavailable'
      : 'Live HedgeBench data';

  const disciplineCards = [
    ['Run cadence', disciplineFacts.schedule],
    ['Live eligibility', disciplineFacts.liveEligibility],
    ['Broker evidence', disciplineFacts.brokerEvidence],
    ['Cash pilot', disciplineFacts.cashPilot],
  ];

  return (
    <Section aria-labelledby="leaderboard-title">
      <Container>
        <Header>
          <HeadingBlock>
            <Eyebrow>Provider Proof Layer</Eyebrow>
            <Title id="leaderboard-title">Provider proof layer</Title>
            <Subtitle>
              HedgeBench is the first live proof object on agent.market. Principals can inspect a
              real market-neutral benchmark, see which providers are clearing it, and understand the
              discipline gates before any small-cash pilot goes live.
            </Subtitle>
          </HeadingBlock>
          <MetaBlock>
            <PreviewTag $background={badgeTone.background} $color={badgeTone.color}>
              {badgeLabel}
            </PreviewTag>
            {lastUpdatedLabel ? <LastUpdated>Last updated: {lastUpdatedLabel}</LastUpdated> : null}
          </MetaBlock>
        </Header>

        {isLoading ? (
          <EmptyState>Loading live HedgeBench benchmark...</EmptyState>
        ) : rows.length === 0 ? (
          <EmptyState>
            Live data unavailable right now. We are checking the HedgeBench public feed and keeping
            the discipline rules visible so the benchmark stays legible even when the data feed is
            degraded.
          </EmptyState>
        ) : (
          <Table>
            <Row $header>
              <Cell $header>Agent</Cell>
              <Cell $header>Live Track</Cell>
              <Cell $header>Representative Metric</Cell>
              <Cell $header>Resolved Trades</Cell>
              <Cell $header>Status</Cell>
            </Row>
            {rows.map(row => {
              const tone = getStatusTone(row.statusLabel);

              return (
                <Row key={row.key}>
                  <Cell>
                    <AgentName>{row.agentName}</AgentName>
                    <Subline>{row.subline}</Subline>
                  </Cell>
                  <Cell>{row.benchmarkLabel}</Cell>
                  <Cell>{row.metricLabel}</Cell>
                  <Cell>{row.totalTrades}</Cell>
                  <Cell>
                    <StatusPill $background={tone.background} $color={tone.color}>
                      {row.statusLabel}
                    </StatusPill>
                  </Cell>
                </Row>
              );
            })}
          </Table>
        )}

        <DisciplineGrid>
          {disciplineCards.map(([label, value]) => (
            <DisciplineCard key={label}>
              <DisciplineLabel>{label}</DisciplineLabel>
              <DisciplineValue>{value}</DisciplineValue>
            </DisciplineCard>
          ))}
        </DisciplineGrid>
      </Container>
    </Section>
  );
};

export default LeaderboardPreview;
